import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, Check, Loader2, AlertCircle, Apple } from 'lucide-react';
import axios from 'axios';
import { PaymentStatusPill, isPaid, isFailed } from '../components/admin/PaymentStatusPill';
import FloatingDonateTab from '../components/FloatingDonateTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatPrice = (n) => `$${parseFloat(n || 0).toFixed(2)}`;

/**
 * Permanent Event Pop-Up Shop — `/popup-shop`.
 *
 * Single global QR target (https://novenahf.org/popup-shop). Pulls from the
 * shared `shop_items` inventory. Quick-checkout: first/last/email only — no
 * shipping (in-person event pickup). Orders tagged source='pop_up_shop',
 * fulfillment_type='in_person_pickup'.
 *
 * When the admin toggles the pop-up OFF, this page shows a friendly inactive
 * message instead of inventory.
 */
const EventPopupShop = () => {
  const [params] = useSearchParams();
  const [state, setState] = useState(null);   // { active: bool, items: [] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cart, setCart] = useState({});       // { item_id: quantity }
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);

  const orderStatus = params.get('status');
  const orderId = params.get('order_id');
  const [confirmedOrder, setConfirmedOrder] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/popup-shop/items`);
      setState(r.data);
    } catch (e) {
      setError('Could not load the pop-up shop. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Anonymous funnel tracking — fire 'scan' once on first page load.
  useEffect(() => {
    const sent = sessionStorage.getItem('nhf_popup_scan_tracked');
    if (sent) return;
    axios.post(`${BACKEND_URL}/api/public/popup-shop/track/scan`).catch(() => {});
    sessionStorage.setItem('nhf_popup_scan_tracked', '1');
  }, []);

  // Fire 'checkout_open' the first time the cart becomes non-empty.
  const checkoutOpenSentRef = React.useRef(false);
  useEffect(() => {
    if (!checkoutOpenSentRef.current && Object.keys(cart).length > 0) {
      checkoutOpenSentRef.current = true;
      axios.post(`${BACKEND_URL}/api/public/popup-shop/track/checkout_open`).catch(() => {});
    }
  }, [cart]);

  useEffect(() => {
    if (orderStatus === 'success' && orderId) {
      // Phase 4: poll every 3s until payment status is final (paid/failed/refunded)
      let attempts = 0;
      const maxAttempts = 60; // 60 * 3s = 3 minutes max
      const poll = () => {
        axios.get(`${BACKEND_URL}/api/public/popup-shop/order/${orderId}`)
          .then(r => {
            setConfirmedOrder(r.data);
            attempts += 1;
            const finalStatuses = ['paid', 'completed', 'failed', 'refunded', 'expired'];
            if (!finalStatuses.includes(r.data?.status) && attempts < maxAttempts) {
              setTimeout(poll, 3000);
            }
          })
          .catch(() => {});
      };
      poll();
    }
  }, [orderStatus, orderId]);

  const updateQty = (itemId, delta) => {
    setCart((prev) => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, current + delta);
      const updated = { ...prev };
      if (next === 0) delete updated[itemId];
      else updated[itemId] = Math.min(next, 20);
      return updated;
    });
  };

  const items = state?.items || [];
  const cartLines = useMemo(() => {
    return Object.entries(cart).map(([id, qty]) => {
      const item = items.find((i) => i.id === id);
      if (!item) return null;
      return { ...item, quantity: qty, line_total: item.price * qty };
    }).filter(Boolean);
  }, [cart, items]);

  const total = cartLines.reduce((s, l) => s + l.line_total, 0);
  const totalQty = cartLines.reduce((s, l) => s + l.quantity, 0);

  const isEmailValid = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const canCheckout = firstName.trim() && lastName.trim() && isEmailValid && cartLines.length > 0;

  const handleCheckout = async () => {
    if (!canCheckout) return;
    setCheckingOut(true);
    // Fire 'payment_start' funnel event — fire-and-forget
    axios.post(`${BACKEND_URL}/api/public/popup-shop/track/payment_start`).catch(() => {});
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/popup-shop/checkout`, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        cart_items: cartLines.map((l) => ({ item_id: l.id, quantity: l.quantity })),
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      alert(e.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setCheckingOut(false);
    }
  };

  // ===== Loading =====
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#BA1826] animate-spin" />
      </div>
    );
  }

  // ===== Network error =====
  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="popup-shop-error">
        <div className="max-w-sm text-center">
          <AlertCircle className="w-10 h-10 text-[#BA1826] mx-auto mb-4" />
          <p className="font-heading text-xl mb-2">Something went wrong</p>
          <p className="text-sm text-white/70">{error}</p>
        </div>
      </div>
    );
  }

  // ===== Inactive state =====
  if (!state?.active) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="popup-shop-inactive">
        <div className="max-w-sm text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-5">
            <ShoppingBag className="w-7 h-7 text-white/70" />
          </div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#BA1826] font-bold mb-2">Novena Humanitarian Foundation</p>
          <h1 className="font-heading text-2xl font-bold mb-3">Pop-up shop is not active right now.</h1>
          <p className="text-sm text-white/70 leading-relaxed mb-5">
            We open this shop during in-person fundraisers and events. Browse our full merchandise selection any time.
          </p>
          <a href="/shop" className="inline-block bg-[#BA1826] text-white font-bold uppercase tracking-wide text-xs px-5 py-2.5 rounded">
            Visit our regular shop
          </a>
        </div>
      </div>
    );
  }

  // ===== Success page =====
  if (orderStatus === 'success') {
    const orderStatusVal = confirmedOrder?.status;
    const paymentConfirmed = isPaid(orderStatusVal);
    const paymentFailed = isFailed(orderStatusVal);
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="popup-shop-success">
        <div className="max-w-sm w-full text-center">
          {paymentFailed ? (
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-white" strokeWidth={3} />
            </div>
          ) : (
            <div className={`w-20 h-20 ${paymentConfirmed ? 'bg-[#BA1826]' : 'bg-amber-500'} rounded-full flex items-center justify-center mx-auto mb-6`}>
              {paymentConfirmed
                ? <Check className="w-10 h-10 text-white" strokeWidth={3} />
                : <Loader2 className="w-10 h-10 text-white animate-spin" strokeWidth={3} />}
            </div>
          )}
          <h1 className="font-heading text-3xl font-bold mb-3" data-testid="popup-shop-success-title">
            {paymentFailed ? 'Payment Issue' : paymentConfirmed ? 'Thank You!' : 'Confirming Payment…'}
          </h1>
          <p className="text-base text-white/85 leading-relaxed mb-4">
            {paymentFailed
              ? 'Your payment did not go through. Please retry or use a different card.'
              : paymentConfirmed
                ? 'Thank you for supporting Novena Humanitarian Foundation.'
                : 'Waiting for Stripe to confirm your payment. This usually takes just a few seconds.'}
          </p>
          {confirmedOrder && (
            <div className="mb-4 flex justify-center">
              <PaymentStatusPill status={orderStatusVal} size="lg" />
            </div>
          )}
          <div className="bg-white text-[#0A0A0A] rounded-lg p-5 mb-6 text-left">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#BA1826] font-bold mb-2">
              {paymentConfirmed ? 'Show this to our team for pickup' : 'Order summary'}
            </p>
            {confirmedOrder ? (
              <>
                <p className="font-mono text-base font-bold mb-1" data-testid="popup-shop-order-number">{confirmedOrder.order_number}</p>
                <p className="text-xs text-[#4B5563] mb-3">{confirmedOrder.buyer_name}</p>
                <div className="space-y-1 text-xs border-t border-[#E5E7EB] pt-2">
                  {(confirmedOrder.line_items || []).map((li, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{li.item_name} × {li.quantity}</span>
                      <span className="font-mono">{formatPrice(li.line_total)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-bold pt-2 border-t border-[#E5E7EB] mt-2">
                    <span>Total</span>
                    <span className="font-mono">{formatPrice(confirmedOrder.total)}</span>
                  </div>
                </div>
                {!paymentConfirmed && !paymentFailed && (
                  <p className="text-[10px] text-amber-700 text-center mt-2">
                    Live status updates every 3 seconds.
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-[#4B5563]">Order ID: <span className="font-mono">{orderId}</span></p>
            )}
          </div>
          <a href="/popup-shop" className="text-xs text-white/60 hover:text-white underline">← Back to pop-up shop</a>
        </div>
      </div>
    );
  }

  const cancelledNote = orderStatus === 'cancelled';

  // ===== Main Storefront =====
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-44" data-testid="popup-shop-page">
      <header className="bg-[#BA1826] py-6 px-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-1">Novena Humanitarian Foundation</p>
        <h1 className="font-heading text-2xl font-bold">Event Pop-Up Shop</h1>
        <p className="text-xs text-white/80 mt-1">In-person pickup · 100% supports our mission</p>
      </header>

      {cancelledNote && (
        <div className="bg-amber-500/10 border-y border-amber-500/30 text-amber-300 text-xs text-center py-2 px-4">
          Checkout cancelled — your cart is still here. Try again when you're ready.
        </div>
      )}

      <main className="max-w-md mx-auto px-4 py-6 space-y-3">
        {items.length === 0 && (
          <p className="text-center text-white/60 text-sm py-8">No items available right now. Check back soon.</p>
        )}
        {items.map((item) => {
          const qty = cart[item.id] || 0;
          const inv = item.inventory || 0;
          const remaining = inv > 0 ? Math.max(0, inv - (item.sold_qty || 0)) : null;
          return (
            <div key={item.id} className="bg-white text-[#0A0A0A] rounded-lg overflow-hidden" data-testid={`popup-shop-item-${item.id}`}>
              <div className="flex">
                {item.image && (
                  <img src={item.image} alt={item.name} className="w-24 h-24 object-cover flex-shrink-0" />
                )}
                <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                  <div>
                    <p className="font-heading text-sm font-bold truncate">{item.name}</p>
                    {item.description && (
                      <p className="text-[10px] text-[#4B5563] line-clamp-2 mt-0.5">{item.description}</p>
                    )}
                    {remaining !== null && remaining <= 5 && remaining > 0 && (
                      <p className="text-[10px] text-amber-700 font-semibold mt-0.5">Only {remaining} left</p>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-bold text-base text-[#BA1826]">{formatPrice(item.price)}</span>
                    {qty === 0 ? (
                      <button
                        onClick={() => updateQty(item.id, 1)}
                        className="bg-[#0A0A0A] text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded"
                        data-testid={`popup-shop-add-${item.id}`}
                      >
                        Add
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => updateQty(item.id, -1)} className="w-7 h-7 bg-[#F1F5F9] rounded flex items-center justify-center"
                          data-testid={`popup-shop-minus-${item.id}`}>
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="font-bold text-sm w-6 text-center" data-testid={`popup-shop-qty-${item.id}`}>{qty}</span>
                        <button onClick={() => updateQty(item.id, 1)} className="w-7 h-7 bg-[#0A0A0A] text-white rounded flex items-center justify-center"
                          data-testid={`popup-shop-plus-${item.id}`}>
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </main>

      {/* Sticky Checkout Bar */}
      {cartLines.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-white/10 p-4 z-40" data-testid="popup-shop-checkout-bar">
          <div className="max-w-md mx-auto space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-white/70">{totalQty} item{totalQty !== 1 ? 's' : ''}</span>
              <span className="font-mono font-bold text-lg" data-testid="popup-shop-total">{formatPrice(total)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name" maxLength={40}
                className="px-3 py-2.5 text-sm bg-white text-[#0A0A0A] rounded focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
                data-testid="popup-shop-first-name" />
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name" maxLength={40}
                className="px-3 py-2.5 text-sm bg-white text-[#0A0A0A] rounded focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
                data-testid="popup-shop-last-name" />
            </div>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email for receipt" maxLength={120}
              className="w-full px-3 py-2.5 text-sm bg-white text-[#0A0A0A] rounded focus:outline-none focus:ring-2 focus:ring-[#BA1826]"
              data-testid="popup-shop-email" />
            <button
              onClick={handleCheckout}
              disabled={checkingOut || !canCheckout}
              className="w-full bg-[#BA1826] text-white font-bold uppercase tracking-wide text-sm py-3 rounded disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="popup-shop-checkout-btn"
            >
              {checkingOut ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              {checkingOut ? 'Opening checkout…' : `Pay ${formatPrice(total)}`}
            </button>
            <p className="text-[9px] text-white/40 text-center flex items-center justify-center gap-1.5">
              <Apple className="w-3 h-3" /> Apple Pay · Google Pay · Card · Secured by Stripe
            </p>
          </div>
        </div>
      )}

      {/* Persistent Donate Now CTA — lifts above checkout bar when cart is active */}
      <FloatingDonateTab lifted={cartLines.length > 0} />
    </div>
  );
};

export default EventPopupShop;
